import type { ReactNode } from 'react';
import type { PushNotificationsController } from '../../hooks/usePushNotifications';

const TOGGLE_BASE_CLASSES = 'rounded-lg border border-input px-3 py-2 text-sm transition-colors';

function SettingSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-0.5 text-sm font-semibold text-foreground">{title}</h4>
      <p className="mb-3 text-xs text-muted-foreground">{description}</p>
      {children}
    </div>
  );
}

export default function NotificationsTab({ push }: { push: PushNotificationsController }) {
  const canEnable = push.supported && push.permissionState !== 'denied';
  const showDisable = push.permissionState === 'granted' && push.appPushEnabled;

  return (
    <div className="space-y-6">
      <SettingSection
        title="Push Notifications"
        description="Receive agent pings even when the PWA is in the background or closed."
      >
        <div
          className="rounded-xl border p-4"
          style={{
            borderColor: 'var(--theme-border)',
            background: 'var(--theme-card)',
          }}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span style={{ fontSize: '1rem' }}>🔔</span>
                <span className="text-sm font-semibold text-foreground">Status: {push.statusLabel}</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{push.statusHint}</p>
              {push.lastError ? (
                <p className="mt-2 text-xs" style={{ color: 'var(--theme-destructive-foreground)' }}>
                  {push.lastError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void push.refresh()}
                disabled={push.isBusy}
                className={`${TOGGLE_BASE_CLASSES} bg-secondary text-secondary-foreground hover:bg-muted disabled:opacity-60`}
              >
                Refresh
              </button>

              {showDisable ? (
                <button
                  type="button"
                  onClick={() => void push.disablePush()}
                  disabled={push.isBusy}
                  className={`${TOGGLE_BASE_CLASSES} bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-60`}
                >
                  {push.isBusy ? 'Disabling…' : 'Disable'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void push.enablePush()}
                  disabled={!canEnable || push.isBusy}
                  className={`${TOGGLE_BASE_CLASSES} bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-60`}
                >
                  {push.isBusy ? 'Enabling…' : 'Enable'}
                </button>
              )}
            </div>
          </div>
        </div>
      </SettingSection>

      <div className="border-t border-border pt-6">
        <SettingSection
          title="Browser Permission"
          description="Chrome controls the final permission state for installed PWAs."
        >
          <div className="rounded-lg border border-input bg-background px-3 py-3 text-xs text-muted-foreground">
            {push.permissionState === 'unsupported' ? (
              <span>This device does not support push notifications.</span>
            ) : push.permissionState === 'denied' ? (
              <span>Notifications are blocked at the browser level. Re-enable them from site or app settings, then come back here.</span>
            ) : push.permissionState === 'default' ? (
              <span>You have not granted notification permission yet. Use the enable button when you're ready.</span>
            ) : (
              <span>Browser permission is granted. You can pause or resume delivery from this screen.</span>
            )}
          </div>
        </SettingSection>
      </div>

      <div className="border-t border-border pt-6">
        <SettingSection
          title="What happens when a push arrives"
          description="Tapping a notification opens the matching private chat and lets the agent know you arrived from push."
        >
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li>• Notification opens the PWA directly to the agent conversation.</li>
            <li>• The app sends a silent arrival hint so the agent can greet naturally.</li>
            <li>• If the browser blocks notifications, delivery will stay off until permission is restored.</li>
          </ul>
        </SettingSection>
      </div>
    </div>
  );
}
